/**
 * Pure elapsed/remaining/overtime maths (BUILD-SPEC.md §4).
 *
 * No clock access anywhere in this file: every function that needs the
 * current time takes `now: number` as a parameter, sourced by the caller
 * from the Live Share timestamp provider (or the local fallback clock).
 */

import type { Session } from "./types";

/**
 * Total seconds this entry has spent speaking, as of `now`.
 *
 * `spentSeconds` is the banked total from previous turns/pauses. If this
 * entry is the one currently speaking (`i === s.idx` and its status is
 * "speaking") and the clock is running (`turnStartedAt !== null`), the
 * elapsed time since `turnStartedAt` is added on top of the banked amount.
 * For every other entry (past, future, or the current entry while paused),
 * only the banked amount counts.
 */
export function elapsedFor(s: Session, i: number, now: number): number {
  const entry = s.queue[i];
  if (!entry) return 0;

  const isLiveCurrentTurn =
    i === s.idx && entry.status === "speaking" && s.turnStartedAt !== null;

  const live = isLiveCurrentTurn ? now - (s.turnStartedAt as number) : 0;
  return entry.spentSeconds + live;
}

/**
 * Seconds remaining for entry `i`, as of `now`. Negative once over time —
 * overtime counts up forever and this function never clamps at zero.
 */
export function remainingFor(s: Session, i: number, now: number): number {
  return limitFor(s, i) - elapsedFor(s, i, now);
}

/** This entry's actual speaking limit: the session default plus any
 * extensions granted to this entry specifically. */
export function limitFor(s: Session, i: number): number {
  const entry = s.queue[i];
  if (!entry) return s.limitSeconds;
  return s.limitSeconds + entry.extensionSeconds;
}

/**
 * Visual/semantic bucket for entry `i`'s current state:
 * - "overtime": remaining < 0 (past the limit, counting up).
 * - "warning":  remaining is at or below 25% of the limit, but not yet over.
 * - "normal":   otherwise.
 */
export function colourState(
  s: Session,
  i: number,
  now: number
): "normal" | "warning" | "overtime" {
  const remaining = remainingFor(s, i, now);
  if (remaining < 0) return "overtime";

  const limit = limitFor(s, i);
  const warningThreshold = limit * 0.25;
  if (remaining <= warningThreshold) return "warning";

  return "normal";
}

/** Sum of elapsed time across every entry in the queue, as of `now` — the
 * meeting's running total. */
export function totalElapsed(s: Session, now: number): number {
  let total = 0;
  for (let i = 0; i < s.queue.length; i++) {
    total += elapsedFor(s, i, now);
  }
  return total;
}

/** Total time budgeted for the whole meeting: every speaker's limit
 * (session default + their own extensions), summed. Equivalent to
 * `speakers × limitSeconds + total extensions`. */
export function budgetSeconds(s: Session): number {
  let total = 0;
  for (let i = 0; i < s.queue.length; i++) {
    total += limitFor(s, i);
  }
  return total;
}
